import { createStorefrontApiClient } from '@shopify/storefront-api-client';

// GraphQL fragment for cart operations
const CartFragment = `
  fragment CartFragment on Cart {
    id
    checkoutUrl
    totalQuantity
    cost {
      totalAmount {
        amount
        currencyCode
      }
    }
    lines(first: 250) {
      edges {
        node {
          id
          quantity
          merchandise {
            ... on ProductVariant {
              id
              title
              priceV2: price {
                amount
                currencyCode
              }
              compareAtPriceV2: compareAtPrice {
                amount
                currencyCode
              }
              image {
                url
              }
              product {
                title
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  }
`;

// Initialize Shopify client
export const client = createStorefrontApiClient({
  storeDomain: `checkout.${process.env.SHOP_NAME}.se`,
  apiVersion: '2025-04',
  publicAccessToken: process.env.SHOPIFY_ACCESS_TOKEN,
  language: 'sv-SE',
});

// Create a new cart
export async function createCart(lineItems = []) {
  const mutation = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          ...CartFragment
        }
        userErrors {
          code
          field
          message
        }
      }
    }
    ${CartFragment}
  `;

  const variables = {
    input: {},
  };

  if (lineItems && lineItems.length > 0) {
    variables.input.lines = lineItems.map((item) => ({
      merchandiseId: item.variantId,
      quantity: item.quantity,
    }));
  }

  const { data } = await client.request(mutation, { variables });

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }

  return normalizeCart(data.cartCreate.cart);
}

// Fetch an existing cart
export async function fetchCart(cartId) {
  const query = `
    query getCart($cartId: ID!) {
      cart(id: $cartId) {
        ...CartFragment
      }
    }
    ${CartFragment}
  `;

  const { data } = await client.request(query, {
    variables: {
      cartId,
    },
  });

  if (!data.cart) {
    throw new Error('Cart not found');
  }

  return normalizeCart(data.cart);
}

// Add items to cart
export async function addLineItems(cartId, lineItems) {
  const mutation = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFragment
        }
        userErrors {
          code
          field
          message
        }
      }
    }
    ${CartFragment}
  `;

  const { data } = await client.request(mutation, {
    variables: {
      cartId,
      lines: lineItems.map((item) => ({
        merchandiseId: item.variantId,
        quantity: item.quantity,
      })),
    },
  });

  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors[0].message);
  }

  return normalizeCart(data.cartLinesAdd.cart);
}

// Remove items from cart
export async function removeLineItems(cartId, lineItemIds) {
  const mutation = `
    mutation cartLinesRemove($cartId: ID!, $lineItemIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineItemIds) {
        cart {
          ...CartFragment
        }
        userErrors {
          code
          field
          message
        }
      }
    }
    ${CartFragment}
  `;

  const { data } = await client.request(mutation, {
    variables: {
      cartId,
      lineItemIds,
    },
  });

  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors[0].message);
  }

  return normalizeCart(data.cartLinesRemove.cart);
}

// Update items in cart
export async function updateLineItems(cartId, lineItems) {
  const mutation = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFragment
        }
        userErrors {
          code
          field
          message
        }
      }
    }
    ${CartFragment}
  `;

  const { data } = await client.request(mutation, {
    variables: {
      cartId,
      lines: lineItems.map((item) => ({
        id: item.id,
        quantity: item.quantity,
      })),
    },
  });

  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors[0].message);
  }

  return normalizeCart(data.cartLinesUpdate.cart);
}

// Helper function to normalize cart data
function normalizeCart(cart) {
  return {
    ...cart,
    lineItems: cart.lines.edges.map((edge) => {
      const { node } = edge;
      return {
        id: node.id,
        title: node.merchandise.product.title,
        quantity: node.quantity,
        variant: {
          id: node.merchandise.id,
          title: node.merchandise.title,
          priceV2: node.merchandise.priceV2,
          compareAtPriceV2: node.merchandise.compareAtPriceV2,
          image: node.merchandise.image,
          selectedOptions: node.merchandise.selectedOptions,
        },
      };
    }),
  };
}
